/**
 * Utilitário para tratamento padronizado de erros da API
 * Padroniza como o Admin deve interpretar respostas de erro
 */

/**
 * Verifica se a resposta é um erro da API
 * @param {*} error - Objeto de erro do axios ou fetch
 * @returns {boolean} True se é um erro de resposta da API
 */
export const isApiError = (error) => {
  return error && error.response && error.response.status;
};

/**
 * Extrai a mensagem de erro da resposta da API
 * Padrão esperado: { message: "string" }
 * @param {*} error - Objeto de erro do axios
 * @returns {string} Mensagem de erro formatada
 */
export const getApiErrorMessage = (error) => {
  // Erro de resposta da API (400, 404, 500, etc)
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  // Fallback para propriedade data como string
  if (typeof error?.response?.data === 'string') {
    return error.response.data;
  }

  // Erro de rede ou timeout
  if (error?.message) {
    return error.message;
  }

  return 'Erro desconhecido ao processar requisição';
};

/**
 * Verifica se o erro é de validação de integridade referencial
 * (tentativa de deletar entidade que está em uso)
 * @param {*} error - Objeto de erro do axios
 * @returns {boolean} True se é erro de integridade referencial
 */
export const isIntegrityError = (error) => {
  const message = getApiErrorMessage(error);
  return message.includes('INTEGRIDADE REFERENCIAL') ||
         message.includes('está vinculado') ||
         message.includes('está em uso') ||
         message.includes('Remova');
};

/**
 * Verifica se o erro é de não encontrado (404)
 * @param {*} error - Objeto de erro do axios
 * @returns {boolean} True se é erro 404
 */
export const isNotFoundError = (error) => {
  return error?.response?.status === 404;
};

/**
 * Verifica se o erro é de validação (400)
 * @param {*} error - Objeto de erro do axios
 * @returns {boolean} True se é erro 400
 */
export const isValidationError = (error) => {
  return error?.response?.status === 400;
};

/**
 * Verifica se o erro é de servidor (500)
 * @param {*} error - Objeto de erro do axios
 * @returns {boolean} True se é erro 500
 */
export const isServerError = (error) => {
  return error?.response?.status === 500;
};

/**
 * Formata a mensagem de erro para exibição ao usuário
 * Remove ruído técnico e deixa claro o que deve ser feito
 * @param {*} error - Objeto de erro do axios
 * @returns {object} { type: string, message: string, action?: string }
 */
export const formatErrorForUser = (error) => {
  const message = getApiErrorMessage(error);
  const status = error?.response?.status;

  // Erro de integridade (400 - tentativa de deletar o que está em uso)
  if (isIntegrityError(error)) {
    return {
      type: 'integrity',
      message: message, // Já vem formatado em português da API
      action: 'Resolva os conflitos listados acima antes de deletar'
    };
  }

  // Erro 404 - não encontrado
  if (isNotFoundError(error)) {
    return {
      type: 'notfound',
      message: message || 'O item que você tenta deletar não existe ou já foi removido',
      action: 'Atualize a página para ver o estado atual'
    };
  }

  // Erro 400 - validação genérica
  if (isValidationError(error)) {
    return {
      type: 'validation',
      message: message || 'Requisição inválida',
      action: 'Verifique os dados e tente novamente'
    };
  }

  // Erro 500 - servidor
  if (isServerError(error)) {
    return {
      type: 'server',
      message: message || 'Erro interno do servidor',
      action: 'Tente novamente em alguns segundos. Se persistir, contate o suporte'
    };
  }

  // Erro genérico (sem resposta HTTP)
  return {
    type: 'unknown',
    message: message || 'Erro desconhecido',
    action: 'Verifique sua conexão e tente novamente'
  };
};

/**
 * Helper para DELETE operations - valida se foi bem-sucedido
 * A API agora retorna:
 * - HTTP 204 (No Content) para sucesso
 * - HTTP 400 (Bad Request) para validação/integridade
 * - HTTP 404 (Not Found) para não encontrado
 * - HTTP 500 (Server Error) para erros do servidor
 * 
 * @param {function} deleteFunction - Função que faz o DELETE
 * @param {*} params - Parâmetros para passar à função de delete
 * @returns {object} { success: boolean, error?: object }
 */
export const handleDeleteOperation = async (deleteFunction, params) => {
  try {
    await deleteFunction(params);
    // Se chegou aqui sem throw, foi 204 No Content (sucesso)
    return { success: true };
  } catch (error) {
    if (isApiError(error)) {
      return {
        success: false,
        error: formatErrorForUser(error),
        rawError: error
      };
    }

    // Erro de rede ou timeout
    return {
      success: false,
      error: {
        type: 'network',
        message: error.message || 'Erro de conexão',
        action: 'Verifique sua conexão e tente novamente'
      },
      rawError: error
    };
  }
};

export default {
  isApiError,
  getApiErrorMessage,
  isIntegrityError,
  isNotFoundError,
  isValidationError,
  isServerError,
  formatErrorForUser,
  handleDeleteOperation
};
